import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type { LogicalAlignment } from '@wix/editor-elements-common-utils';
import {
  getInputHeight,
  getLabelPaddingsValues,
  getRequiredIndicationDisplay,
  withFallbackValues,
  addUnitToEveryField,
  convertPhysicalInputAlignmentToLogical,
  convertPhysicalInputAlignmentToDirection,
  getHeightInPixels,
  withSiteDirection,
  extractUnit,
} from '@wix/editor-elements-common-utils';
import type {
  ITextAreaInputMapperProps,
  TextAreaInputDefinition,
  TextAreaInputCSSVars,
  TextAreaInputCssCarmiData,
  TextAreaInputStyleProperties,
} from '../TextAreaInput.types';
import { translationKeys } from './constants';

const formatValueWithUnit = (valueWithUnit: string | number) => {
  const value: string =
    typeof valueWithUnit === 'number'
      ? valueWithUnit.toString()
      : valueWithUnit;
  const numericValue = parseFloat(value);
  const unit = extractUnit(value);
  return `${numericValue}${unit}`;
};

export const props = withCompInfo<
  ITextAreaInputMapperProps,
  TextAreaInputDefinition
>()(
  ['compData', 'compProps', 'skin', 'translate'],
  ({ compData, compProps, skin, translate }) => ({
    skin:
      skin === 'ResponsiveTextAreaDefaultSkin'
        ? 'ResponsiveTextAreaDefaultSkin'
        : 'TextAreaDefaultSkin',
    value: compData.value || '',
    label: compData.label,
    placeholder: compData.placeholder || compProps.placeholder,
    readOnly: compProps.readOnly || false,
    required: compProps.required || false,
    isDisabled: compProps.isDisabled || false,
    maxLength: compData.maxLength,
    errorMessageType: compProps.errorMessageType,
    translations: {
      errorMessage: translate(
        translationKeys.NAMESPACE,
        translationKeys.INLINE_ERROR_MESSAGE_DEFAULT_ERROR_MESSAGE,
      ),
    },
  }),
);

export const css = withCompInfo<
  TextAreaInputCSSVars,
  TextAreaInputDefinition,
  TextAreaInputCssCarmiData
>()(
  [
    'compLayout',
    'compSingleLayout',
    'compProps',
    'compData',
    'experiments',
    'styleProperties',
    'isMobileView',
    'hasResponsiveLayout',
    'isOneDocMigrated',
  ],
  (
    {
      compLayout,
      compSingleLayout,
      compProps,
      compData,
      styleProperties,
      isMobileView,
      hasResponsiveLayout,
      isOneDocMigrated,
    },
    cssVars,
  ) => {
    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      inputDirection = 'inherit',
      errorDirection = 'inherit',
    } = compData;
    const {
      align,
      labelMargin,
      labelPadding,
      textPadding = 3,
    } = styleProperties as TextAreaInputStyleProperties;

    const sharedProps = {
      ...cssVars,
      '--direction': withSiteDirection(direction),
      '--labelDirection': labelDirection,
      '--inputDirection': inputDirection,
      '--errorDirection': errorDirection,
      '--align': align as LogicalAlignment,
      '--textPaddingTop': hasResponsiveLayout ? '0.75em' : '3px',
      '--textPaddingStart': `${formatValueWithUnit(textPadding)}`,
      '--textPaddingEnd':
        align === 'center' ? `${formatValueWithUnit(textPadding)}` : '10px',
      '--labelMarginBottom': `${formatValueWithUnit(labelMargin)}`,
      '--requiredIndicationDisplay':
        getRequiredIndicationDisplay(styleProperties),
      ...addUnitToEveryField(
        getLabelPaddingsValues({
          labelPadding,
          align: align as LogicalAlignment,
        }),
      ),
    };

    if (hasResponsiveLayout) {
      return sharedProps;
    } else {
      const height = isOneDocMigrated
        ? getHeightInPixels(compSingleLayout)
        : compLayout.height;

      return {
        ...sharedProps,
        ...(!isOneDocMigrated && {
          height: 'auto',
        }),
        '--inputHeight': getInputHeight({
          inputHeightProps: compProps,
          height,
          isMobileView,
        }),
      };
    }
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: ['labelMargin', 'labelPadding', 'textPadding', 'textAlignment'],
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'textAlignment', target: 'align' }],
        enhancer: convertPhysicalInputAlignmentToLogical,
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'textAlignment', target: 'direction' }],
        enhancer: convertPhysicalInputAlignmentToDirection,
      },
    ]),
  ],
);

export default createComponentMapperModel({ props, css });
